import { Cursor, Sparkle, Trash, Warning } from '@phosphor-icons/react';
import { useGetBrandProfile } from '@reelplicate/api-client/brand-profiles';
import {
  DATA_TYPE_LABELS,
  DATA_TYPES,
  type FlowNode,
  getNodeDefinition,
  type InputPort,
  type NodeDefinition,
} from '@reelplicate/flows';
import {
  CATEGORY_TINT,
  cn,
  DataTypeChip,
  EmptyState,
  Field,
  FLOW_TINTS,
  IconButton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  TextInput,
  Tooltip,
  Well,
} from '@reelplicate/ui';
import { Link as RouterLink } from 'react-router';

import { CATEGORY_NAME, ioLabel, NODE_ICONS } from '../../catalogue-ui';
import { useEditor, useEditorStore } from '../editor-store';
import { NodeItems } from '../run/node-items';
import { useAnalysis } from '../use-analysis';
import { ConfigForm } from './config-form';

/** What a node does today, where that's less than its description promises. */
const NODE_NOTES: Record<string, string> = {
  'output.schedulePublish':
    'For now it puts posts on the Calendar at the chosen times; publishing to your accounts comes later.',
};

/** Nodes whose output the brand profile shapes. */
const BRAND_AWARE = new Set([
  'generate.script',
  'generate.hookVariations',
  'generate.captionHashtags',
  'guardrail.brandGuardrails',
]);

/**
 * The right-hand panel (Figma editor, Inspector): the selected node's header,
 * then Setup (name, what it does, types in and out), Configure (its settings)
 * and Test (the last test run's items - with the run engine).
 */
export function InspectorPanel({ workspaceId }: { workspaceId: string }) {
  const store = useEditorStore();
  const selectedIds = useEditor((state) => state.selectedNodeIds);
  const node = useEditor((state) =>
    state.selectedNodeIds.length === 1
      ? state.graph.nodes.find((n) => n.id === state.selectedNodeIds[0])
      : undefined,
  );
  const definition = node && getNodeDefinition(node.type, node.typeVersion);

  return (
    <aside
      aria-label="Inspector"
      className="bg-bg-surface border-border-subtle flex w-85 shrink-0 flex-col overflow-y-auto border-l p-5"
    >
      {!node || !definition ? (
        <EmptyState
          icon={Cursor}
          titleAs="h2"
          title={
            selectedIds.length > 1
              ? `${selectedIds.length} nodes selected`
              : 'Select a node'
          }
          description={
            selectedIds.length > 1
              ? 'Pick one node to set it up. Press Delete to remove the selection.'
              : 'Click a node on the canvas to set it up, or add one from the library.'
          }
        />
      ) : (
        <NodeInspector
          key={node.id}
          workspaceId={workspaceId}
          node={node}
          definition={definition}
          onDelete={() => store.getState().remove([node.id])}
        />
      )}
    </aside>
  );
}

function NodeInspector({
  workspaceId,
  node,
  definition,
  onDelete,
}: {
  workspaceId: string;
  node: FlowNode;
  definition: NodeDefinition;
  onDelete: () => void;
}) {
  const store = useEditorStore();
  const { estimate, issues } = useAnalysis();
  const warnings = issues.filter(
    (issue) => issue.severity === 'warning' && issue.nodeId === node.id,
  );
  const tint = FLOW_TINTS[CATEGORY_TINT[definition.category]];
  const Icon = NODE_ICONS[definition.icon] ?? Sparkle;
  const title = node.label ?? definition.label;
  const counts = estimate.nodes[node.id];
  const totalIn = counts ? Object.values(counts.in).reduce((a, b) => a + b, 0) : 0;
  const totalOut = counts ? Object.values(counts.out).reduce((a, b) => a + b, 0) : 0;

  return (
    <Stack gap={5}>
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-md',
            tint.soft,
          )}
        >
          <Icon aria-hidden className={cn('size-5', tint.text)} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <h2 className="text-heading-h4 text-text-primary truncate">{title}</h2>
          <p className="text-caption text-text-secondary">
            {CATEGORY_NAME[definition.category]} · {ioLabel(definition)}
          </p>
        </div>
        <Tooltip content="Delete node">
          <IconButton
            icon={Trash}
            size="sm"
            aria-label="Delete node"
            onClick={onDelete}
          />
        </Tooltip>
      </div>

      <Tabs defaultValue="configure">
        <TabList variant="pill" aria-label="Node" className="w-full [&>*]:flex-1">
          <Tab value="setup">Setup</Tab>
          <Tab value="configure">Configure</Tab>
          <Tab value="test">Test</Tab>
        </TabList>

        <TabPanel value="setup" className="pt-5">
          <Stack gap={5}>
            <Field
              label="Name on the canvas"
              optional
              helper={`Leave empty to show “${definition.label}”.`}
            >
              <TextInput
                size="sm"
                placeholder={definition.label}
                value={node.label ?? ''}
                onChange={(event) => store.getState().rename(node.id, event.target.value)}
              />
            </Field>
            <Stack gap={1}>
              <span className="text-label-md text-text-primary">What it does</span>
              <p className="text-body-sm text-text-secondary">{definition.description}</p>
              {NODE_NOTES[definition.type] && (
                <p className="text-body-sm text-text-secondary">
                  {NODE_NOTES[definition.type]}
                </p>
              )}
            </Stack>
            <Stack gap={2}>
              <span className="text-label-md text-text-primary">Takes</span>
              <PortTypes
                ports={definition.inputs.filter((p) => p.accepts !== 'trigger')}
              />
            </Stack>
            <Stack gap={1}>
              <span className="text-label-md text-text-primary">Hands on</span>
              <p className="text-body-sm text-text-secondary">
                {ioLabel(definition).split(' → ')[1]}
              </p>
            </Stack>
          </Stack>
        </TabPanel>

        <TabPanel value="configure" className="pt-5">
          <Stack gap={5}>
            {counts && totalIn > 0 && (
              <p className="text-caption text-text-secondary">
                About {totalIn} in → {totalOut} out, from this flow’s estimate.
              </p>
            )}
            {BRAND_AWARE.has(definition.type) && (
              <BrandRules workspaceId={workspaceId} type={definition.type} />
            )}
            {warnings.map((issue, i) => (
              <Well key={`${issue.code}-${i}`} tone="warning" icon={Warning}>
                {issue.message}
              </Well>
            ))}
            <ConfigForm
              workspaceId={workspaceId}
              definition={definition}
              config={node.config}
              onChange={(config) => store.getState().setConfig(node.id, config)}
            />
          </Stack>
        </TabPanel>

        <TabPanel value="test" className="pt-5">
          <NodeItems nodeId={node.id} />
        </TabPanel>
      </Tabs>
    </Stack>
  );
}

function PortTypes({ ports }: { ports: readonly InputPort[] }) {
  if (ports.length === 0)
    return (
      <p className="text-body-sm text-text-secondary">Nothing - it starts the flow.</p>
    );
  return (
    <Stack gap={2}>
      {ports.map((port) => (
        <div key={port.id} className="flex flex-wrap items-center gap-1.5">
          {ports.length > 1 && (
            <span className="text-caption text-text-secondary">{port.label}:</span>
          )}
          {port.accepts === 'any' ? (
            <span className="text-body-sm text-text-secondary">
              {port.variant === 'required'
                ? 'Any variant set'
                : 'Anything, passed through'}
            </span>
          ) : (
            DATA_TYPES.filter(
              (type) => port.accepts !== 'trigger' && port.accepts.includes(type),
            ).map((type) => (
              <DataTypeChip key={type} type={type}>
                {DATA_TYPE_LABELS[type]}
              </DataTypeChip>
            ))
          )}
        </div>
      ))}
    </Stack>
  );
}

/** Figma "Brand claims apply": what the brand profile feeds into this node. */
function BrandRules({ workspaceId, type }: { workspaceId: string; type: string }) {
  const { data } = useGetBrandProfile(workspaceId, {
    // Optional context: a missing profile shouldn't take the inspector down.
    query: { enabled: Boolean(workspaceId), throwOnError: false, retry: false },
  });
  if (!data) return null;
  const claims = data.approvedClaims.length;
  const prohibited = data.prohibited.length;
  if (claims + prohibited === 0 && type === 'guardrail.brandGuardrails') {
    return (
      <Well tone="warning" icon={Warning} title="No guardrails yet - Step skipped">
        <Stack gap={2}>
          <p>Your brand profile has no claims or banned phrases, so every post passes.</p>
          <RouterLink
            to="/settings/brand-profile"
            className="text-label-sm text-text-link w-fit hover:underline"
          >
            Add guardrails
          </RouterLink>
        </Stack>
      </Well>
    );
  }
  if (claims + prohibited === 0) {
    return (
      <Well tone="warning" icon={Warning} title="No brand rules yet">
        Add approved claims and prohibited phrases in Settings → Brand profile so this
        node can use them.
      </Well>
    );
  }
  return (
    <Well tone="brand" title="Brand claims apply">
      From your brand profile: {claims} approved {claims === 1 ? 'claim' : 'claims'},{' '}
      {prohibited} prohibited {prohibited === 1 ? 'phrase' : 'phrases'}.
    </Well>
  );
}
